import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import DrawWorkFlow from "../../components/Workflow/DrawWorkFlow";
import AgentService from "../../services/api/agents";
import { API_RESPONSE } from "../../settings/shared/constants";
import DOMPurify from 'dompurify';
import WorkflowHeader from "../../components/common/WorkflowHeader";

// Security: Sanitize text input to prevent XSS
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return 'Untitled Project';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

const AgentWorkflow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: projectIdFromPath } = useParams(); // Get projectId from URL path like /project/:id
  const projectIdFromQuery = searchParams.get('projectId') || searchParams.get('id'); // Also check query params
  const projectId = projectIdFromPath || projectIdFromQuery; // Priority: path param > query param
  const [projectIdFromWorkflow, setProjectIdFromWorkflow] = useState(null);

  const [state, setState] = useState({
    isWorkflowRunning: false,
    hasAgents: false,
    hasTasks: false,
    hasInputs: false,
    activeButton: null, // 'run', 'save', 'clear', 'console', or 'close'
    projectData: null,
    projectName: 'Untitled Project',
    loading: true,
    workflowEventStatus: null, // current workflow event status
    workflowError: null, // error message for failures
    workflowResult: null, // result data for success
    isWorkflowSaved: false // Execute button disabled until workflow is explicitly saved
  });
  
  // Ref to store timeout for clearing completed status
  const completedStatusTimeoutRef = useRef(null);
  const podControlRef = useRef(null);


  // Fetch project data if projectId is provided
  useEffect(() => {
    if (!projectId) {
      // No projectId means starting a new workflow - set loading to false immediately
      setState(prevState => ({
        ...prevState,
        loading: false,
        projectData: null, // Explicitly set to null for new workflow
        isWorkflowSaved: false // New workflow is not saved yet
      }));
      return;
    }
    
    let isMounted = true;
    const controller = new AbortController();

    async function fetchProject() {
      try {
        const postData = { id: projectId };
        const response = await AgentService.getProject(0, postData);

        if (!isMounted) return;
        
        if (response?.status === API_RESPONSE.SUCCESS && response?.projectDTO) {
          const sanitizedName = sanitizeText(response.projectDTO.name);
          
          // Pass the entire response to DrawWorkFlow so it can access workFlowTemplateDTO
          
          // Create a new object to ensure we're not passing a reference that might be modified
          const fullResponse = {
            ...response,
            projectDTO: response.projectDTO,
            workFlowTemplateDTO: response.workFlowTemplateDTO,
            status: response.status
          };
          
          setState(prevState => {
            const newState = {
              ...prevState,
              projectData: fullResponse, // Pass entire response, not just projectDTO
              projectName: sanitizedName || 'Untitled Project',
              loading: false,
              isWorkflowSaved: false // Execute button disabled until user explicitly saves
            };
            return newState;
          });
        } else {
          setState(prevState => ({ ...prevState, loading: false }));
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("AgentWorkflow - Failed to fetch project:", error);
        setState(prevState => ({ ...prevState, loading: false }));
      }
    }

    fetchProject();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [projectId]);


  // Listen for workflow state changes
  useEffect(() => {
    function handleWorkflowRun(e) {
      setState(prevState => ({
        ...prevState,
        isWorkflowRunning: true,
        activeButton: 'run',
        workflowEventStatus: 'workflow_started',
        workflowError: null,
        workflowResult: null
      }));
    }

    function handlePodControlStatus(e) {
      const status = e?.detail?.status;
      podControlRef.current = status;
      if (status === 'Running' || status === 'Stopping') {
        setState(prevState => ({ ...prevState, isWorkflowRunning: true, activeButton: 'run' }));
      } else if (status === 'Stopped' || status === 'Failed') {
        setState(prevState => ({ ...prevState, isWorkflowRunning: false, activeButton: 'run' }));
      }
    }

    function handleWorkflowStop(e) {
      if (podControlRef.current === 'Running' || podControlRef.current === 'Stopping') return;
      // Clear any existing timeout
      if (completedStatusTimeoutRef.current) {
        clearTimeout(completedStatusTimeoutRef.current);
        completedStatusTimeoutRef.current = null;
      }

      // Only clear status if it's not request_completed (keep it visible for a while)
      setState(prevState => {
        if (prevState.workflowEventStatus === 'request_completed') {
          // Keep the completed status visible, just update running state
          return {
            ...prevState,
            isWorkflowRunning: false,
            activeButton: 'run'
          };
        } else {
          // Clear status for other cases
          return {
            ...prevState,
            isWorkflowRunning: false,
            activeButton: 'run',
            workflowEventStatus: null,
            workflowError: null,
            workflowResult: null
          };
        }
      });
    }

    function handleWorkflowEvent(e) {
      const { eventType, errorMessage, result } = e.detail || {};
      if (eventType) {
        // Clear any existing timeout
        if (completedStatusTimeoutRef.current) {
          clearTimeout(completedStatusTimeoutRef.current);
          completedStatusTimeoutRef.current = null;
        }
        
        // Format result for display - extract only the result text
        let formattedResult = null;
        if (result) {
          let extractedResult = null;
          
          // If result is an array, get the first item's result field
          if (Array.isArray(result) && result.length > 0) {
            const firstItem = result[0];
            extractedResult = firstItem.result || firstItem.reason || firstItem;
          }
          // If result is an object, extract the 'result' field
          else if (typeof result === 'object') {
            extractedResult = result.result || result.data || result.reason || result;
          }
          // If it's already a string, use it directly
          else {
            extractedResult = result;
          }
          
          // Format the extracted result
          if (extractedResult) {
            if (typeof extractedResult === 'object') {
              // If still an object, try to get string value
              if (extractedResult.result) {
                formattedResult = String(extractedResult.result);
              } else if (extractedResult.reason) {
                formattedResult = String(extractedResult.reason);
              } else if (extractedResult.data) {
                formattedResult = String(extractedResult.data);
              } else {
                // Last resort: use default message
                formattedResult = 'Query executed successfully';
              }
            } else {
              formattedResult = String(extractedResult);
            }
          } else {
            formattedResult = 'Query executed successfully';
          }
        } else if (eventType === 'request_completed') {
          formattedResult = 'Query executed successfully';
        }
        
        setState(prevState => ({
          ...prevState,
          workflowEventStatus: eventType,
          workflowError: errorMessage || (eventType.includes('error') || eventType.includes('failed') ? 'An error occurred' : null),
          workflowResult: formattedResult
        }));
        
        // If it's request_completed, keep it visible for 10 seconds before clearing
        if (eventType === 'request_completed') {
          completedStatusTimeoutRef.current = setTimeout(() => {
            setState(prevState => ({
              ...prevState,
              workflowEventStatus: null,
              workflowError: null,
              workflowResult: null
            }));
            completedStatusTimeoutRef.current = null;
          }, 10000); // 10 seconds
        }
      }
    }

    function handleRunWorkflow(e) {
      // Clear any existing timeout when starting a new workflow
      if (completedStatusTimeoutRef.current) {
        clearTimeout(completedStatusTimeoutRef.current);
        completedStatusTimeoutRef.current = null;
      }
      
      // When user clicks "Send query", set status to Pending
      setState(prevState => ({
        ...prevState,
        workflowEventStatus: 'Pending',
        workflowError: null,
        workflowResult: null
      }));
    }

    function handleAgentsChanged(e) {
      const { hasAgents, hasTasks } = e.detail || {};
      setState(prevState => ({
        ...prevState,
        hasAgents,
        hasTasks
      }));
    }

    function handleInputsChanged(e) {
      const { hasInputs } = e.detail || {};
      setState(prevState => ({
        ...prevState,
        hasInputs
      }));
    }

    function handleConsoleClose(e) {
      setState(prevState => ({
        ...prevState,
        activeButton: prevState.activeButton === 'console' ? null : prevState.activeButton
      }));
    }

    function handleWorkflowSaved(e) {
      setState(prevState => ({
        ...prevState,
        isWorkflowSaved: true
      }));
    }

    function handleClearWorkflow(e) {
      setState(prevState => ({
        ...prevState,
        isWorkflowSaved: false // Workflow is no longer saved after clearing
      }));
    }

    // Listen for workflow events
    window.addEventListener('workflow-run', handleWorkflowRun);
    window.addEventListener('workflow-stop', handleWorkflowStop);
    window.addEventListener('workflow-pod-status', handlePodControlStatus);
    window.addEventListener('workflow-agents-changed', handleAgentsChanged);
    window.addEventListener('workflow-inputs-changed', handleInputsChanged);
    window.addEventListener('close-console-drawer', handleConsoleClose);
    window.addEventListener('workflow-event', handleWorkflowEvent);
    window.addEventListener('run-workflow', handleRunWorkflow);
    window.addEventListener('workflow-saved', handleWorkflowSaved);
    window.addEventListener('clear-workflow', handleClearWorkflow);

    return () => {
      // Cleanup timeout on unmount
      if (completedStatusTimeoutRef.current) {
        clearTimeout(completedStatusTimeoutRef.current);
        completedStatusTimeoutRef.current = null;
      }
      
      window.removeEventListener('workflow-run', handleWorkflowRun);
      window.removeEventListener('workflow-stop', handleWorkflowStop);
      window.removeEventListener('workflow-pod-status', handlePodControlStatus);
      window.removeEventListener('workflow-agents-changed', handleAgentsChanged);
      window.removeEventListener('workflow-inputs-changed', handleInputsChanged);
      window.removeEventListener('close-console-drawer', handleConsoleClose);
      window.removeEventListener('workflow-event', handleWorkflowEvent);
      window.removeEventListener('run-workflow', handleRunWorkflow);
      window.removeEventListener('workflow-saved', handleWorkflowSaved);
      window.removeEventListener('clear-workflow', handleClearWorkflow);
    };
  }, []);

  // Listen for ProjectID updates emitted from DrawWorkFlow (save creates id via replaceState).
  useEffect(() => {
    const handler = (e) => {
      const nextId = e?.detail?.projectId ?? e?.detail?.id ?? null;
      if (!nextId) return;
      setProjectIdFromWorkflow(String(nextId));
    };
    window.addEventListener('workflow-project-id', handler);
    return () => window.removeEventListener('workflow-project-id', handler);
  }, []);

  const setActiveButton = (buttonName) => {
    setState(prevState => ({
      ...prevState,
      activeButton: buttonName
    }));
  };

  // Function to clear workflow
  const handleClearWorkflow = () => {
    setActiveButton('clear');
    // Dispatch event to clear workflow
    const event = new CustomEvent('clear-workflow');
    window.dispatchEvent(event);
  };

  // Remove hash from URL if present - use multiple strategies to ensure it's removed
  useEffect(() => {
    const removeHash = () => {
      if (window.location.hash) {
        const newUrl = window.location.pathname + window.location.search;
        // Use replaceState first (doesn't reload page)
        window.history.replaceState(null, '', newUrl);
        // If hash still exists, use location.replace as fallback
        if (window.location.hash) {
          window.location.replace(newUrl);
        }
      }
    };

    // Remove hash immediately on mount
    removeHash();

    // Remove hash after short delays to catch any async additions
    const timeouts = [
      setTimeout(removeHash, 0),
      setTimeout(removeHash, 10),
      setTimeout(removeHash, 50),
      setTimeout(removeHash, 100),
      setTimeout(removeHash, 200)
    ];

    // Listen for hash changes
    const handleHashChange = () => {
      removeHash();
    };
    window.addEventListener('hashchange', handleHashChange);

    // Listen for popstate (back/forward navigation)
    const handlePopState = () => {
      removeHash();
    };
    window.addEventListener('popstate', handlePopState);

    // Periodic check as a safety net (every 500ms for first 2 seconds)
    const intervalId = setInterval(() => {
      if (window.location.hash) {
        removeHash();
      }
    }, 500);

    // Clear interval after 2 seconds
    const clearIntervalTimeout = setTimeout(() => {
      clearInterval(intervalId);
    }, 2000);

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
      clearTimeout(clearIntervalTimeout);
      clearInterval(intervalId);
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
      <div style={{ width: '100%', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <WorkflowHeader
            projectName={state.projectName}
            isWorkflowRunning={state.isWorkflowRunning}
            hasAgents={state.hasAgents}
            hasTasks={state.hasTasks}
            hasInputs={state.hasInputs}
            activeButton={state.activeButton}
            workflowEventStatus={state.workflowEventStatus}
            workflowError={state.workflowError}
            workflowResult={state.workflowResult}
            isWorkflowSaved={state.isWorkflowSaved}
            projectId={
                projectId ||
                projectIdFromWorkflow ||
                state.projectData?.projectDTO?.id ||
                state.projectData?.projectDTO?.projectInfo?.id
            }
            orchestratorId={state.projectData?.workFlowTemplateDTO?.id || state.projectData?.projectDTO?.workFlowTemplateDTO?.id}
            onSetActiveButton={setActiveButton}
            onExecute={() => {
              if (state.isWorkflowRunning) {
                setActiveButton('run');
                window.dispatchEvent(new CustomEvent('stop-workflow-pod', {
                  detail: { source: 'execute-button' },
                }));
                return;
              }
              if (!state.hasAgents && !state.hasTasks) return;
              setActiveButton('run');
              window.dispatchEvent(new CustomEvent('check-and-execute-workflow'));
            }}
            onGenerate={() => {
              if (!state.hasAgents && !state.hasTasks) return;
              setActiveButton('generate');
              const event = new CustomEvent('generate-workflow');
              window.dispatchEvent(event);
            }}
            onSave={(options) => {
              if (!state.hasAgents && !state.hasTasks) return;
              setActiveButton('save');
              const event = new CustomEvent('save-workflow', { detail: options || {} });
              window.dispatchEvent(event);
            }}
            onPublish={(meta) => {
              if (!state.hasAgents && !state.hasTasks) return;
              setActiveButton('publish');
              window.dispatchEvent(new CustomEvent('publish-workflow', { detail: meta || {} }));
            }}
            onClear={handleClearWorkflow}
            onConsole={() => {
              if (!state.hasInputs) return;
              setActiveButton('console');
              const event = new CustomEvent('open-console-drawer');
              window.dispatchEvent(event);
            }}
            onClose={() => {
              setActiveButton('close');
              navigate('/apps');
            }}
            isChatVisible={false}
            onToggleChat={null}
        />

        {/* DrawWorkFlow Component */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <DrawWorkFlow appInfo={state.projectData} />
        </div>
      </div>
  );
};

export default AgentWorkflow;